import { LayerGroup } from "react-leaflet"
import RouteLines from "@/features/route/RouteLines"
import RoutePoints from "@/features/route/RoutePoints"
import { buildVesselsGeojson } from "@/utils/vesselHelper"
import { useViewportWindow } from "@/hooks/useViewportWindow"
import VesselIcon from "@/features/vessel/VesselIcon"

import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import localizedFormat from "dayjs/plugin/localizedFormat"
dayjs.extend(utc)
dayjs.extend(localizedFormat)

export default function PathfinderController({
  mapBounds,
  sliderPosition,
  markedVesselNames,
  recenterToPoint,
  setMarkedVesselNames,
  setOpenDrawer,
  setOpenVesselList,
  map,
  calculatorDepartureTime,
  calculatorBasespeed,
  setEtaResponse,
  speedProfileData,
  consumptionProfileData,
  setRouteConsumption,
  switchVoyagePlannerActive,
  switchVesselFiltersActive,
  setPathfinderResponse,
  setEtaError,
  setSelectedVessel,
  setSelectedVesselAnchor,
  draftRoutes,
  plannerDraftRouteId,
  openWaypointCard,
  plannerVesselSettings,
  cwViaAreasGeojson,
  applyRnwSafety,
  applyEca,
  avoidHra,
  avoidJwla,
  avoidCaa,
}) {
  const { norm, viewportTick } = useViewportWindow(map)

  // Drafts are visible as soon as either surface that owns them is active
  // (voyage planner or vessel filters); import previews render regardless —
  // their lifetime is the open import dialog.
  const visibleRoutes =
    switchVoyagePlannerActive || switchVesselFiltersActive
      ? draftRoutes ?? []
      : (draftRoutes ?? []).filter((route) => route?.source === "import")
  // Sketches carry no times: no vessel icon, and no need for a slider position.
  const renderRoutes = sliderPosition.label
    ? visibleRoutes
    : visibleRoutes.filter((route) => route?.sketch)
  const timedRoutes = renderRoutes.filter((route) => !route?.sketch)

  if (renderRoutes.length === 0) {
    return null
  }

  let vessels = timedRoutes.length
    ? buildVesselsGeojson({ routes: timedRoutes, sliderPosition })
    : []
  // Remount key input: one segment per rendered draft.
  const vesselSignature = timedRoutes
    .map((route) => {
      const first = route?.points?.geojson?.features?.[0] ?? null
      const coords = first?.geometry?.coordinates ?? []
      const ts = first?.properties?.timestamp ?? "na"
      return `${route?.points?.routeId}:${ts}:${coords?.[0] ?? "na"},${coords?.[1] ?? "na"}`
    })
    .join("|")
  return (
    <LayerGroup key={"Pathfinder"}>
      <RoutePoints
        vesselRoutes={renderRoutes}
        plannerDraftRouteId={plannerDraftRouteId}
        mapBounds={mapBounds}
        calculatorDepartureTime={calculatorDepartureTime}
        calculatorBasespeed={calculatorBasespeed}
        setEtaResponse={setEtaResponse}
        speedProfileData={speedProfileData}
        consumptionProfileData={consumptionProfileData}
        setRouteConsumption={setRouteConsumption}
        setPathfinderResponse={setPathfinderResponse}
        setEtaError={setEtaError}
        norm={norm}
        viewportTick={viewportTick}
        openWaypointCard={openWaypointCard}
        plannerVesselSettings={plannerVesselSettings}
        cwViaAreasGeojson={cwViaAreasGeojson}
        applyRnwSafety={applyRnwSafety}
        applyEca={applyEca}
        avoidHra={avoidHra}
        avoidJwla={avoidJwla}
        avoidCaa={avoidCaa}
      />
      <RouteLines
        vesselRoutes={renderRoutes}
        plannerDraftRouteId={plannerDraftRouteId}
        mapBounds={mapBounds}
        calculatorDepartureTime={calculatorDepartureTime}
        calculatorBasespeed={calculatorBasespeed}
        setEtaResponse={setEtaResponse}
        speedProfileData={speedProfileData}
        consumptionProfileData={consumptionProfileData}
        setRouteConsumption={setRouteConsumption}
        setPathfinderResponse={setPathfinderResponse}
        setEtaError={setEtaError}
        norm={norm}
        viewportTick={viewportTick}
        plannerVesselSettings={plannerVesselSettings}
        cwViaAreasGeojson={cwViaAreasGeojson}
        applyRnwSafety={applyRnwSafety}
        applyEca={applyEca}
        avoidHra={avoidHra}
        avoidJwla={avoidJwla}
        avoidCaa={avoidCaa}
      />

      {vessels && vessels.length > 0 && (
        <VesselIcon
          key={`PathfinderVesselIcon-${sliderPosition?.label ?? "na"}-${vessels.length}-${vesselSignature}`}
          vessels={vessels}
          markedVesselNames={markedVesselNames}
          recenterToPoint={recenterToPoint}
          setMarkedVesselNames={setMarkedVesselNames}
          setOpenDrawer={setOpenDrawer}
          setOpenVesselList={setOpenVesselList}
          setSelectedVessel={setSelectedVessel}
          setSelectedVesselAnchor={setSelectedVesselAnchor}
        />
      )}
    </LayerGroup>
  )
}
